import React, { useState } from "react";
import "./GridCommandV3.scss";

/**
 * Grid Command V3 - Reduced cognitive load, game-first design
 * Focuses on visual hierarchy and playful elements over raw stats
 */
const GridCommandV3: React.FC = () => {
    const [selectedGame, setSelectedGame] = useState<string>("all");
    const [showGameSelector, setShowGameSelector] = useState(false);

    // Mock game data with actual logos
    const games = [
        {
            id: "valorant",
            name: "Valorant",
            logo: "/images/games/valorant-logo.png",
            activeQuests: 5,
            brandDrops: 2,
            color: "#FF4655",
        },
        {
            id: "fortnite",
            name: "Fortnite",
            logo: "/images/games/fortnite-logo.png",
            activeQuests: 8,
            brandDrops: 3,
            color: "#318CFE",
        },
        {
            id: "apex",
            name: "Apex Legends",
            logo: "/images/games/apex-logo.png",
            activeQuests: 4,
            brandDrops: 1,
            color: "#DA292A",
        },
        {
            id: "cod",
            name: "Call of Duty",
            logo: "/images/games/cod-logo.png",
            activeQuests: 6,
            brandDrops: 2,
            color: "#00A652",
        },
    ];

    const selectedGameData = games.find(g => g.id === selectedGame);

    return (
        <div className="grid-command-v3">
            {/* Streamlined Header with User Profile */}
            <header className="command-header">
                <div className="user-profile">
                    <div className="avatar-container">
                        <img src="/placeholder-avatar.jpg" alt="User" className="avatar" />
                        <div className="level-badge">42</div>
                    </div>
                    <div className="profile-info">
                        <span className="username">COMMANDER</span>
                        <div className="xp-mini">
                            <div className="xp-bar">
                                <div className="xp-fill" style={{width: "67%"}} />
                            </div>
                            <span className="xp-text">67% to 43</span>
                        </div>
                    </div>
                </div>

                <div className="primary-stats">
                    <div className="stat-capsule">
                        <span className="stat-icon">⚡</span>
                        <span className="stat-value">1,058</span>
                        <span className="stat-label">FUEL</span>
                    </div>
                    <div className="guild-capsule">
                        <span className="guild-icon">🛡️</span>
                        <span className="guild-rank">#7</span>
                        <span className="guild-name">APEX PREDATORS</span>
                    </div>
                </div>

                <div className="header-actions">
                    <button className="notification-btn">
                        <span className="notification-icon">🔔</span>
                        <span className="notification-badge">3</span>
                    </button>
                </div>
            </header>

            {/* Game Selector Bar - Visual Priority */}
            <div className="game-selector-section">
                <div className="game-quick-select">
                    <button
                        className={`game-tile ${selectedGame === "all" ? "active" : ""}`}
                        onClick={() => setSelectedGame("all")}
                    >
                        <div className="tile-content">
                            <span className="tile-icon">🎮</span>
                            <span className="tile-label">ALL</span>
                            <div className="tile-indicators">
                                <span className="quest-count">26</span>
                                <span className="brand-dot">🔴</span>
                            </div>
                        </div>
                    </button>

                    {games.slice(0, 4).map(game =>
                        <button
                            key={game.id}
                            className={`game-tile ${selectedGame === game.id ? "active" : ""}`}
                            onClick={() => setSelectedGame(game.id)}
                            style={{borderColor: selectedGame === game.id ? game.color : "transparent"}}
                        >
                            <div className="tile-content">
                                <img src={game.logo} alt={game.name} className="game-logo" />
                                <div className="tile-indicators">
                                    <span className="quest-count">{game.activeQuests}</span>
                                    {game.brandDrops > 0 && <span className="brand-indicator">⚡</span>}
                                </div>
                            </div>
                        </button>)}

                    <button
                        className="more-games-btn"
                        onClick={() => setShowGameSelector(!showGameSelector)}
                    >
                        <span>+</span>
                    </button>
                </div>

                {selectedGame !== "all" &&
          <div className="selected-game-info">
              <span className="game-status">🟢 TRACKING ACTIVE</span>
              <span className="last-played">Last played: 2 hours ago</span>
          </div>
                }
            </div>

            {/* Main Content Area - Less Dense, More Visual */}
            <div className="command-content">

                {/* Featured Brand Drop - Hero Section */}
                <div className="featured-drop">
                    <div className="drop-backdrop">
                        <img src="/images/brand-drops/redbull-bg.jpg" alt="Red Bull" />
                        <div className="drop-overlay" />
                    </div>
                    <div className="drop-content">
                        <div className="drop-header">
                            <span className="drop-badge">🔥 BRAND DROP LIVE</span>
                            <span className="drop-timer">ENDS IN 2D 14H</span>
                        </div>
                        <img src="/images/brands/redbull-logo.png" alt="Red Bull" className="brand-logo" />
                        <h2 className="drop-title">CLUTCH PLAYS CHALLENGE</h2>
                        <div className="drop-games">
                            {games.slice(0, 3).map(game =>
                                <img key={game.id} src={game.logo} alt={game.name} className="supported-game" />)}
                        </div>
                        <div className="drop-rewards">
                            <div className="reward-badge reward-badge--xp">
                                <span className="reward-value">2.5K</span>
                                <span className="reward-label">XP</span>
                            </div>
                            <div className="reward-badge reward-badge--fuel">
                                <span className="reward-value">1K</span>
                                <span className="reward-label">FUEL</span>
                            </div>
                            <div className="reward-badge reward-badge--special">
                                <span className="reward-icon">🎁</span>
                                <span className="reward-label">EXCLUSIVE</span>
                            </div>
                        </div>
                        <button className="drop-cta">
                            <span className="cta-text">START CHALLENGE</span>
                            <span className="cta-arrow">→</span>
                        </button>
                    </div>
                </div>

                {/* Mission Grid - Visual Cards */}
                <div className="mission-grid">

                    {/* Active Quest Cards - Game Icons Prominent */}
                    <div className="quest-card quest-card--active">
                        <div className="quest-game">
                            <img src="/images/games/valorant-logo.png" alt="Valorant" className="quest-game-logo" />
                        </div>
                        <div className="quest-content">
                            <h3 className="quest-title">ACE MACHINE</h3>
                            <div className="quest-progress">
                                <div className="progress-ring">
                                    <svg viewBox="0 0 36 36">
                                        <path className="ring-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                        <path className="ring-fill" strokeDasharray="40, 100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                    </svg>
                                    <span className="progress-text">2/5</span>
                                </div>
                            </div>
                            <div className="quest-reward">
                                <span className="reward-xp">750 XP</span>
                            </div>
                        </div>
                        <div className="quest-action">
                            <button className="action-btn">CONTINUE</button>
                        </div>
                    </div>

                    {/* Guild Challenge Card */}
                    <div className="quest-card quest-card--guild">
                        <div className="guild-badge">
                            <span className="badge-icon">👥</span>
                            <span className="badge-label">GUILD</span>
                        </div>
                        <div className="quest-content">
                            <h3 className="quest-title">SQUAD SYNERGY</h3>
                            <div className="guild-leaderboard-mini">
                                <div className="position">
                                    <span className="rank">#7</span>
                                    <span className="movement">↑2</span>
                                </div>
                                <div className="guild-xp">45,280 XP</div>
                            </div>
                            <div className="contributors">
                                <div className="contributor-avatars">
                                    <img src="/avatar1.jpg" alt="Player" />
                                    <img src="/avatar2.jpg" alt="Player" />
                                    <img src="/avatar3.jpg" alt="Player" />
                                    <span className="more">+12</span>
                                </div>
                            </div>
                        </div>
                        <div className="quest-action">
                            <button className="action-btn action-btn--guild">VIEW GUILD</button>
                        </div>
                    </div>

                    {/* Daily Quest - Simplified */}
                    <div className="quest-card quest-card--daily">
                        <div className="daily-indicator">
                            <span className="daily-icon">☀️</span>
                            <span className="reset-timer">RESETS IN 4H</span>
                        </div>
                        <div className="quest-content">
                            <h3 className="quest-title">FIRST BLOOD</h3>
                            <div className="quest-games-mini">
                                <div className="game-check game-check--done">
                                    <img src="/images/games/valorant-logo.png" alt="Valorant" />
                                    <span className="check">✓</span>
                                </div>
                                <div className="game-check">
                                    <img src="/images/games/apex-logo.png" alt="Apex" />
                                </div>
                                <div className="game-check">
                                    <img src="/images/games/cod-logo.png" alt="COD" />
                                </div>
                            </div>
                            <div className="quest-reward">
                                <span className="reward-xp">250 XP</span>
                            </div>
                        </div>
                    </div>

                    {/* Brand Quest Card - Visual Focus */}
                    <div className="quest-card quest-card--brand">
                        <div className="brand-header">
                            <img src="/images/brands/snapple-logo.png" alt="Snapple" className="brand-mini-logo" />
                            <span className="brand-type">INSTANT WIN</span>
                        </div>
                        <div className="quest-content">
                            <h3 className="quest-title">SNAPPLE STREAK</h3>
                            <div className="instant-win-progress">
                                <div className="win-slots">
                                    <div className="slot slot--won">✓</div>
                                    <div className="slot slot--won">✓</div>
                                    <div className="slot">?</div>
                                </div>
                                <span className="win-text">1 MORE FOR PRIZE!</span>
                            </div>
                        </div>
                        <div className="quest-action">
                            <button className="action-btn action-btn--brand">PLAY NOW</button>
                        </div>
                    </div>
                </div>

                {/* Live Activity Feed - Simplified */}
                <div className="activity-rail">
                    <h3 className="rail-title">HAPPENING NOW</h3>
                    <div className="activity-stream">
                        <div className="activity-bubble activity-bubble--achievement">
                            <span className="bubble-icon">🏆</span>
                            <span className="bubble-text">New badge unlocked!</span>
                        </div>
                        <div className="activity-bubble activity-bubble--guild">
                            <span className="bubble-icon">⬆️</span>
                            <span className="bubble-text">Guild ranked up to #6</span>
                        </div>
                        <div className="activity-bubble activity-bubble--friend">
                            <span className="bubble-icon">🎮</span>
                            <span className="bubble-text">DeadEye is online</span>
                        </div>
                        <div className="activity-bubble activity-bubble--reward">
                            <span className="bubble-icon">💰</span>
                            <span className="bubble-text">+500 FUEL earned</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Floating Action Button */}
            <button className="fab-play">
                <span className="fab-icon">▶</span>
                <span className="fab-text">PLAY</span>
            </button>
        </div>
    );
};

export default GridCommandV3;